import type { ToolHandle } from '@toolproof-core/genesis/_contract/_index';
import { buildPanelShape } from '@toolproof-core/visualization/_pure/shapes';
import {
    defineEntityConstellationDerivation,
    defineEntityItemDerivation,
    toEntityItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import type { ItemCollection } from '@toolproof-core/visualization/_contract/collection';

import { getResourceTypeRingRadius } from '@/workflow/_pure/config/derivationConfig';
import { deriveResourceTypeAngularInfo } from '@/workflow/_pure/derivation/resourceTypeAngularPlacement';
import { withConceptResourceRadials } from '@/workflow-runner/conceptResources';
import { CONCEPT_INVOCATION_EXAMPLES } from '@/workflow-runner/_data/conceptInvocationExamples';
import type { Config } from '@/workflow/_contract/config';
import type { CosmosDataModel } from '@/workflow/_contract/cosmosData';
import type { RuntimeState } from '@/workflow-runner/_contract/runtime';

export const INVOCATION_STAGES = ['selected', 'inputs', 'invoked', 'outputs'] as const;
export const INVOCATION_STAGE_MS = 650;
export const INVOCATION_ITEM_KEY = 'item:entity:ToolInvocation';
const RING_COLLECTION = 'collection:entity:ToolInvocation';

export function conceptInvocationSelection(state: RuntimeState, handles: readonly ToolHandle[]) {
    const arrangement = state.conceptArrangement;
    if (arrangement?.content !== 'tool-invocation') throw new Error('Concept invocation requires a Tool invocation arrangement.');
    if (arrangement.invocationStage !== 'tour') return { handle: 'TOOL-NaturalAdd' as const, stage: arrangement.invocationStage };
    const progress = state.conceptInvocation?.revision === state.episode?.revision ? state.conceptInvocation : undefined;
    const handle = handles[progress?.index ?? 0];
    const stage = INVOCATION_STAGES[progress?.stageIndex ?? 0];
    if (!handle || !stage) throw new Error('Concept invocation tour has an invalid Tool or stage index.');
    return { handle, stage };
}

export function conceptInvocationExample(cosmosData: CosmosDataModel, handle: ToolHandle) {
    const tool = cosmosData.toolMap.get(handle);
    const example = CONCEPT_INVOCATION_EXAMPLES[handle];
    if (!tool || tool.isTemplate || !example) throw new Error(`Concept invocation requires an example for available concrete Tool ${handle}.`);
    const entries = (direction: 'input' | 'output') => {
        const roles = direction === 'input' ? tool.roleSpec.inputRoleByName : tool.roleSpec.outputRoleByName;
        const values = direction === 'input' ? example.inputs : example.outputs;
        if (Object.keys(values).length !== Object.keys(roles).length
            || Object.keys(values).some(role => !(role in roles))) {
            throw new Error(`Concept invocation example does not match ${handle} ${direction} roles.`);
        }
        return Object.entries(roles).map(([role, spec]) => {
            const value = values[role];
            if (spec.resourceTypeHandle === 'TYPE-Natural'
                ? typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0
                : spec.resourceTypeHandle !== 'TYPE-Boolean' || typeof value !== 'boolean') {
                throw new Error(`Concept invocation has an invalid ${spec.resourceTypeHandle} for ${handle}.${role}.`);
            }
            const intrinsic = Object.entries(tool.intrinsicInputByRoleName ?? {}).find(([name]) => name === role)?.[1];
            if (direction === 'input' && intrinsic !== undefined && intrinsic !== value) {
                throw new Error(`Concept invocation cannot override intrinsic input ${handle}.${role}.`);
            }
            return { role, value, direction, resourceTypeHandle: spec.resourceTypeHandle };
        });
    };
    return { tool, inputs: entries('input'), outputs: entries('output') };
}

export function invocationGeometry(config: Config) {
    const panel = config.domainPresentation.entities.workflowStateEntries.item.shape;
    const sphereRadius = config.domainPresentation.entities.resourceTypes.item?.shape?.radius ?? 0;
    const spacing = panel.height * 3;
    const firstRadius = getResourceTypeRingRadius(config) + sphereRadius + spacing;
    const ringRadius = firstRadius + spacing * 2;
    return { panel, spacing, firstRadius, ringRadius };
}

function invocationEntries(config: Config, selection: ReturnType<typeof conceptInvocationSelection>, cosmosData: CosmosDataModel) {
    const { spacing, firstRadius } = invocationGeometry(config);
    const { inputs, outputs } = conceptInvocationExample(cosmosData, selection.handle);
    const counts = new Map<string, number>();
    return [...inputs, ...(selection.stage === 'outputs' ? outputs : [])].map(entry => {
        const slot = `${entry.direction}:${entry.resourceTypeHandle}`;
        const index = counts.get(slot) ?? 0;
        counts.set(slot, index + 1);
        if (index >= 2) throw new Error('Concept invocation ring requires at most two same-type roles in each direction.');
        return { ...entry, radius: firstRadius + (entry.direction === 'input' ? index : 3 + index) * spacing,
            itemKey: `item:entity:ToolInvocation:${selection.handle.slice(5)}:${entry.role}` as const };
    });
}

export function deriveInvocationCollections(config: Config, selection: ReturnType<typeof conceptInvocationSelection>, cosmosData: CosmosDataModel) {
    if (selection.stage === 'selected') return [];
    const { panel } = invocationGeometry(config);
    const settings = config.domainPresentation.entities.workflowStateEntries.item;
    const entries = invocationEntries(config, selection, cosmosData);
    const types = [...new Set(entries.map(entry => entry.resourceTypeHandle))];
    return withConceptResourceRadials(config, [
        toEntityItemCollectionDerivation({
            itemCollectionKey: RING_COLLECTION,
            itemDerivations: [defineEntityItemDerivation({
                item: { itemKind: 'entity', itemKey: INVOCATION_ITEM_KEY,
                    name: 'ToolInvocation', description: `One application of ${selection.handle}` },
                parametricState: { visualStyle: config.domainPresentation.entities.toolSteps.item.visualStyle },
            })],
        }),
        ...types.map(type => toEntityItemCollectionDerivation({
            itemCollectionKey: `collection:entity:Resource:${type}`,
            itemDerivations: entries.filter(entry => entry.resourceTypeHandle === type).map(entry => defineEntityItemDerivation({
                item: { itemKind: 'entity', itemKey: entry.itemKey,
                    name: String(entry.value), description: `${entry.direction}: ${entry.role} = ${entry.value}` },
                parametricState: {
                    shape: { ...buildPanelShape({ ...panel, curvatureRadius: entry.radius }), appearanceMode: 'filled' },
                    visualStyle: settings[entry.direction].visualStyle,
                    label: {
                        onItem: { ...settings.label?.onItem, text: String(entry.value) },
                        aboveItem: { ...settings.label?.aboveItem,
                            text: `${entry.direction === 'input' ? 'Input' : 'Output'}: ${entry.role}` },
                    },
                },
            })),
        })),
    ]);
}

export function deriveInvocationConstellations(config: Config, selection: ReturnType<typeof conceptInvocationSelection>,
    itemCollections: ItemCollection[], cosmosData: CosmosDataModel) {
    if (selection.stage === 'selected') return [];
    const { spacing, ringRadius } = invocationGeometry(config);
    const tube = config.domainPresentation.entities.toolSteps;
    const entries = invocationEntries(config, selection, cosmosData);
    const { angleByHandle } = deriveResourceTypeAngularInfo({ config, itemCollections });
    return [
        defineEntityConstellationDerivation({
            constellation: { constellationKind: 'entity', constellationKey: 'constellation:entity:ToolInvocation',
                itemCollectionKey: RING_COLLECTION, anchor: { itemKey: INVOCATION_ITEM_KEY, anchorKey: 'center' } },
            parametricState: { layout: { layoutKind: 'concentric-ring', anchor: { anchorKind: 'origin' },
                startRadius: ringRadius, ringGap: spacing, ringCount: 1,
                ringTube: { tubeRadius: tube.tubeRadius, radialSegments: tube.radialSegments,
                    tubularSegments: tube.tubularSegments } } },
        }),
        ...[...new Set(entries.map(entry => entry.resourceTypeHandle))].map(type => {
            const members = entries.filter(entry => entry.resourceTypeHandle === type);
            const angle = angleByHandle.get(type);
            if (angle === undefined) throw new Error(`Concept Tool invocation requires ${type}.`);
            const first = members[0]!;
            return defineEntityConstellationDerivation({
                constellation: { constellationKind: 'entity', constellationKey: `constellation:entity:Resource:${type}`,
                    itemCollectionKey: `collection:entity:Resource:${type}`,
                    anchor: { itemKey: first.itemKey, anchorKey: 'center' } },
                parametricState: { layout: { layoutKind: 'spoke',
                    anchor: { anchorKind: 'parametric', parametricKind: 'ring-intersection',
                        ringItemKey: `item:entity:${type}`,
                        anglePolicy: { anglePolicyKind: 'match-item-in-ring', matchItemKey: `item:entity:${type}` },
                        radialOffset: first.radius - getResourceTypeRingRadius(config) },
                    direction: [Math.cos(angle), 0, Math.sin(angle)], spacing,
                    itemOffsets: members.map(entry => entry.radius - first.radius) } },
            });
        }),
    ];
}
